// Every numeric limit in one place (API.md section 3, "Input limits").

export const LIMITS = {
  userName: 120,
  email: 254,
  /** v3 D3: at least 12 everywhere. Existing shorter passwords keep working until changed (login never checks the length). */
  passwordMin: 12,
  passwordMax: 200,
  orgName: 200,
  contractName: 200,
  contractReference: 120,
  funder: 200,
  funderContactName: 120,
  partnersMax: 20,
  partner: 200,
  description: 10_000,
  boilerplate: 10_000,
  envelopeCentsMax: 100_000_000_000,
  cofinancingPctMax: 100,
  torTitle: 255,
  activityCode: 16,
  location: 200,
  participants: 2_000,
  budgetCentsMax: 100_000_000_000,
  sectionsMax: 20,
  sectionTitle: 120,
  blocksPerSection: 50,
  blockText: 20_000,
  costLinesMax: 200,
  costLineLabel: 255,
  costLineUnit: 32,
  quantityMax: 1_000,
  unitCostCentsMax: 100_000_000,
  expertName: 120,
  positionTitle: 120,
  /** A person's title, for example "Dr" or "Ambassador". */
  personTitle: 40,
  /** A speaker's short biography. */
  personBio: 2_000,
  dayBudgetMax: 1_000,
  feeRateCentsMax: 10_000_000,
  transitionNote: 4_000,
  comment: 2_000,
  contractNote: 1_000,
  searchQuery: 100,
  activityPageDefault: 50,
  activityPageMax: 100,

  // ----- v2 (API.md 6-7, ARCHITECTURE 15) -----
  phone: 40,
  expertise: 2_000,
  languages: 200,
  availabilityNote: 500,
  url: 500,
  website: 300,
  debrief: 10_000,
  actualCentsMax: 100_000_000_000,
  contributorsMax: 50,
  runDayExpertsMax: 20,
  runDayDatesMax: 31,
  workNote: 10_000,
  workHoursMin: 0.5,
  workHoursMax: 24,
  timesheetEntriesMax: 200,
  eventTitle: 200,
  eventDescription: 4_000,
  capacityMax: 5_000,
  /** v2 forms; the v3 RSVP and feedback forms use questionsMaxV3. */
  questionsMax: 10,
  questionLabel: 200,
  questionOptionsMin: 2,
  questionOptionsMax: 10,
  questionOption: 100,
  answerText: 1_000,
  feedbackComment: 2_000,
  accessNeeds: 500,
  organisationName: 200,
  registrationsMax: 5_000,
  inviteContactsMax: 500,
  contactNotes: 2_000,
  stakeholderOrgName: 200,
  stakeholderOrgNotes: 2_000,
  contactsPageDefault: 50,
  contactsPageMax: 200,
  deliverableTitle: 255,
  deliverableText: 4_000,
  dataPointsMax: 30,
  dataPointLabel: 120,
  dataPointNote: 300,
  dataPointValueMax: 1_000_000_000_000,
  resultCode: 16,
  resultStatement: 1_000,
  /** short name of a result or an indicator */
  logframeShortName: 80,
  /** the name of one result level ("Specific objective") */
  resultLevelName: 40,
  indicatorName: 500,
  indicatorUnit: 32,
  meansOfVerification: 1_000,
  indicatorValueAbsMax: 1_000_000_000_000,
  resultsMax: 60,
  indicatorsMax: 150,
  readingNote: 1_000,
  /** the named outside source of a reading */
  readingSourceName: 200,
  /** why a reading was corrected or withdrawn (LF5), and why a report was signed off with open items (RE1) */
  reasonText: 500,
  reportTitle: 200,

  // ----- v3 (SPEC-v3 7) -----
  /** v3: 20 questions per RSVP or feedback form (SPEC-v3 7). */
  questionsMaxV3: 20,
  /** Round 5: the invitation text at the top of the RSVP form. */
  rsvpIntro: 2_000,
  codeTtlMinutes: 15,
  codeMaxAttempts: 5,
  loginLinkTtlMinutes: 15,
  trustedDeviceDays: 30,
  backupCodes: 10,
  uploadMaxMb: 50,
  imageMaxMb: 10,
  cvMaxMb: 20,
  importMaxGb: 5,
  chunkMb: 8,
  cvBatchMax: 50,
  contactImportMaxRows: 20_000,
  contactImportMaxMb: 10,
  bulkInviteMax: 50,
  searchMaxPerType: 20,
  searchMaxTotal: 100,
  shareLinkMaxDays: 365,
  maxExpertDaysMonth: 22,
  pageDefault: 50,
  pageMax: 200,
  notificationTitle: 200,
  notificationBody: 2_000,
  activityCodeV3: 20,
  lineOfWorkName: 200,
  lineOfWorkProof: 2000,
  bannerMinWidth: 1200,
  bannerMinHeight: 300,
  zipMaxEntries: 20_000,
  mailAttachmentsMaxMb: 20,
} as const;

export const TIMING = {
  sessionDays: 30,
  /** A session ends this many days after sign-in, however active it is. */
  sessionMaxDays: 90,
  sessionTouchAfterMs: 60 * 60 * 1000,
  resetTokenMs: 60 * 60 * 1000,
  /** M18 (Marius's wireframes, retest 30 Oct): an invitation to join works for 14 days. */
  invitationMs: 14 * 24 * 60 * 60 * 1000,
  defaultWaitThresholdDays: 6,
  /** v2: dashboard "upcoming events" window (API.md 6.2) */
  upcomingEventDays: 14,
  /** v2: "Debrief missing" after this many days past the activity's end (API.md 6.2) */
  debriefMissingAfterDays: 7,
  /** v3: "Keep me signed in" unchecked = browser-session cookie with a 12-hour sliding server life (SPEC-v3 D6). */
  shortSessionMs: 12 * 60 * 60 * 1000,
  /** v3: two-step must be completed within 5 minutes of the password or link step. */
  mfaPendingMs: 5 * 60 * 1000,
  /** v3: SSO state/nonce/verifier rows live 10 minutes. */
  authFlowMs: 10 * 60 * 1000,
  /** v3: unlinked uploads are deleted after 24 hours. */
  unlinkedFileMs: 24 * 60 * 60 * 1000,
  /** v3: chunked upload sessions expire after 24 hours. */
  uploadSessionMs: 24 * 60 * 60 * 1000,
  /** v3: data requests are due 30 days after receipt. */
  dataRequestDays: 30,
} as const;

/** Public form rate limits (ARCHITECTURE 20.2). In memory, per process. */
export const PUBLIC_FORM_LIMITS = {
  getPerIp: { max: 600, windowMs: 10 * 60 * 1000 },
  postPerIpAndToken: { max: 200, windowMs: 10 * 60 * 1000 },
  postPerToken: { max: 2_000, windowMs: 60 * 60 * 1000 },
} as const;
